import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { BellRing, Clock3, Link2, MessageSquareMore, RefreshCw, Send, ShieldAlert } from "lucide-react";
import { getFacilities, getFacilityFeatureFlags } from "../../lib/services/facilities";
import { issueFamilyStatusLink, revokeFamilyStatusLink } from "../../lib/services/familyStatus";
import {
  getPatientCommsBoard,
  queuePatientCommsDispatch,
  updatePatientCommsStatus,
  type PatientCommsStatusCode,
} from "../../lib/services/patientComms";

const STATUS_OPTIONS: Array<{ value: PatientCommsStatusCode; label: string }> = [
  { value: "checked_in", label: "Checked In" },
  { value: "pre_op", label: "Pre-Op" },
  { value: "in_procedure", label: "In Procedure" },
  { value: "in_recovery", label: "In Recovery" },
  { value: "ready_for_pickup", label: "Ready for Pickup" },
  { value: "discharged", label: "Discharged" },
  { value: "delayed", label: "Delayed" },
];

function labelize(value: string): string {
  return value.replaceAll("_", " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

export function PatientCommsBoardPage() {
  const [selectedFacility, setSelectedFacility] = useState("");
  const [draftStatuses, setDraftStatuses] = useState<Record<string, PatientCommsStatusCode>>({});
  const [selectedContacts, setSelectedContacts] = useState<Record<string, string[]>>({});
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const facilitiesQuery = useQuery({
    queryKey: ["patient-comms-facilities"],
    queryFn: getFacilities,
  });
  const featureFlagsQuery = useQuery({
    queryKey: ["patient-comms-feature-flags"],
    queryFn: () => getFacilityFeatureFlags(),
  });
  const boardQuery = useQuery({
    queryKey: ["patient-comms-board", selectedFacility],
    queryFn: () => getPatientCommsBoard(selectedFacility || undefined),
  });

  const statusMutation = useMutation({
    mutationFn: async (payload: { visitId: string; statusCode: PatientCommsStatusCode }) =>
      await updatePatientCommsStatus(payload.visitId, payload.statusCode),
    onSuccess: async () => {
      await boardQuery.refetch();
    },
  });
  const dispatchMutation = useMutation({
    mutationFn: async (payload: { visitId: string; statusCode: PatientCommsStatusCode; contactIds: string[] }) =>
      await queuePatientCommsDispatch(payload.visitId, payload.statusCode, payload.contactIds),
    onSuccess: async () => {
      await boardQuery.refetch();
    },
  });
  const issueLinkMutation = useMutation({
    mutationFn: async (payload: { visitId: string; contactId?: string }) =>
      await issueFamilyStatusLink(payload.visitId, payload.contactId),
    onSuccess: async () => {
      await boardQuery.refetch();
    },
  });
  const revokeLinkMutation = useMutation({
    mutationFn: async (shareLinkId: string) => await revokeFamilyStatusLink(shareLinkId),
    onSuccess: async () => {
      await boardQuery.refetch();
    },
  });

  const patientCommsEnabled = useMemo(
    () => (featureFlagsQuery.data ?? []).some((flag) => flag.feature_key === "patient_comms" && flag.enabled),
    [featureFlagsQuery.data],
  );

  if (!patientCommsEnabled && featureFlagsQuery.isSuccess) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900">
        Patient/family communication is not enabled for this tenant.
      </div>
    );
  }

  const visits = boardQuery.data?.visits ?? [];

  async function handleStatusUpdate(visitId: string) {
    const statusCode = draftStatuses[visitId] ?? "checked_in";
    setMessage(null);
    setError(null);
    try {
      await statusMutation.mutateAsync({ visitId, statusCode });
      setMessage(`Updated visit ${visitId.slice(0, 8)} to ${labelize(statusCode)}.`);
    } catch (mutationError) {
      setError(mutationError instanceof Error ? mutationError.message : "Failed to update patient communication status");
    }
  }

  async function handleDispatch(visitId: string) {
    const statusCode = draftStatuses[visitId] ?? "checked_in";
    setMessage(null);
    setError(null);
    try {
      const response = await dispatchMutation.mutateAsync({
        visitId,
        statusCode,
        contactIds: selectedContacts[visitId] ?? [],
      }) as { queued_count?: number };
      setMessage(`Queued ${response.queued_count ?? 0} generic update${response.queued_count === 1 ? "" : "s"} for visit ${visitId.slice(0, 8)}.`);
    } catch (mutationError) {
      setError(mutationError instanceof Error ? mutationError.message : "Failed to queue patient communication dispatch");
    }
  }

  async function handleIssueLink(visitId: string, contactId?: string) {
    setMessage(null);
    setError(null);
    try {
      const response = await issueLinkMutation.mutateAsync({ visitId, contactId });
      const absolutePath = typeof window !== "undefined" ? `${window.location.origin}${response.path}` : response.path;
      if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(absolutePath);
      }
      setMessage(`Issued family status link for visit ${visitId.slice(0, 8)} and copied it to the clipboard.`);
    } catch (mutationError) {
      setError(mutationError instanceof Error ? mutationError.message : "Failed to issue family status link");
    }
  }

  async function handleRevokeLink(shareLinkId: string) {
    setMessage(null);
    setError(null);
    try {
      await revokeLinkMutation.mutateAsync(shareLinkId);
      setMessage("Revoked the active family status link.");
    } catch (mutationError) {
      setError(mutationError instanceof Error ? mutationError.message : "Failed to revoke family status link");
    }
  }

  return (
    <div className="space-y-6">
      <header className="v11-workspace-header">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-widest text-[var(--brand-secondary)]">
              Facility operations
            </p>
            <h1 className="font-brand text-2xl font-bold text-[var(--brand-deep)]">Patient &amp; Family Communication</h1>
            <p className="mt-1 text-sm text-slate-600">
              Staff-facing board for visit status updates and PHI-safe outbound communication logging. Sprint 2 stays bounded to generic message templates and in-app follow-on notifications.
            </p>
          </div>
          <button
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700"
            onClick={() => void boardQuery.refetch()}
          >
            <RefreshCw size={16} className="mr-2 inline-block" />
            Refresh Board
          </button>
        </div>
        {message && <p className="mt-3 text-sm text-emerald-700">{message}</p>}
        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      </header>

      <section className="v11-section p-5">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">Today&apos;s Board</h2>
            <p className="mt-1 text-sm text-slate-600">Visits, contacts, latest status events, and recent generic dispatches.</p>
          </div>
          {facilitiesQuery.data && facilitiesQuery.data.length > 1 && (
            <select
              value={selectedFacility}
              onChange={(event) => setSelectedFacility(event.target.value)}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700"
            >
              <option value="">All facilities</option>
              {facilitiesQuery.data.map((facility) => (
                <option key={facility.facility_id} value={facility.facility_id}>
                  {facility.facility_name}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="mt-4 grid gap-4">
          {visits.map((visit) => {
            const currentStatus = draftStatuses[visit.visit_id] ?? visit.latest_status_event?.status_code ?? "checked_in";
            const eligibleContacts = visit.contacts.filter((contact) => contact.consent_status === "granted" && contact.preferred_channel !== "none");
            const chosenContacts = selectedContacts[visit.visit_id] ?? eligibleContacts.map((contact) => contact.contact_id);

            return (
              <article key={visit.visit_id} className="v11-dashboard-card p-4">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-lg font-bold text-slate-900">{visit.facility_name}</h3>
                      <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-600">
                        {labelize(visit.visit_type)}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-slate-600">
                      Visit {visit.visit_id.slice(0, 8)} · {visit.survey_date}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2 text-sm">
                      <span className="rounded-full bg-blue-50 px-3 py-1 font-semibold text-blue-700">
                        Latest: {visit.latest_status_event ? labelize(visit.latest_status_event.status_code) : "No status yet"}
                      </span>
                      <span className="rounded-full bg-emerald-50 px-3 py-1 font-semibold text-emerald-700">
                        Contacts: {visit.contacts.length}
                      </span>
                      <span className="rounded-full bg-violet-50 px-3 py-1 font-semibold text-violet-700">
                        Dispatches: {visit.recent_dispatches.length}
                      </span>
                    </div>
                  </div>

                  <div className="v11-mini-metric text-left lg:w-[22rem]">
                    <label className="block text-xs font-black uppercase tracking-widest text-slate-500">Next Status</label>
                    <select
                      className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700"
                      value={currentStatus}
                      onChange={(event) =>
                        setDraftStatuses((current) => ({
                          ...current,
                          [visit.visit_id]: event.target.value as PatientCommsStatusCode,
                        }))
                      }
                    >
                      {STATUS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>

                    <div className="mt-3 flex flex-wrap gap-2">
                      <button className="btn-primary text-sm" onClick={() => void handleStatusUpdate(visit.visit_id)}>
                        <Clock3 size={15} className="mr-2 inline-block" />
                        Save Status
                      </button>
                      <button
                        className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700"
                        onClick={() => void handleDispatch(visit.visit_id)}
                        disabled={eligibleContacts.length === 0}
                      >
                        <Send size={15} className="mr-2 inline-block" />
                        Queue Update
                      </button>
                    </div>
                    {eligibleContacts.length === 0 && (
                      <p className="mt-2 text-xs text-amber-700">No consented SMS/email contacts are available for dispatch.</p>
                    )}

                    <div className="mt-4 rounded-lg border border-blue-100 bg-blue-50 p-3">
                      <label className="block text-xs font-black uppercase tracking-widest text-blue-700">Family Status Link</label>
                      {visit.active_share_link ? (
                        <div className="mt-2 space-y-2 text-sm text-blue-900">
                          <p>
                            Active link expires {new Date(visit.active_share_link.expires_at).toLocaleString()}.
                          </p>
                          <p className="text-xs text-blue-700">
                            Access count: {visit.active_share_link.access_count}
                            {visit.active_share_link.last_accessed_at
                              ? ` · Last opened ${new Date(visit.active_share_link.last_accessed_at).toLocaleString()}`
                              : ""}
                          </p>
                          <button
                            className="rounded-lg border border-blue-200 px-3 py-2 text-sm font-semibold text-blue-800"
                            onClick={() => void handleRevokeLink(visit.active_share_link!.share_link_id)}
                          >
                            <ShieldAlert size={15} className="mr-2 inline-block" />
                            Revoke Link
                          </button>
                        </div>
                      ) : (
                        <div className="mt-2 space-y-2">
                          <p className="text-xs text-blue-700">
                            Issue a PHI-safe public status page for a consented family contact. The raw link is shown only once and copied to the clipboard.
                          </p>
                          <button
                            className="rounded-lg border border-blue-200 px-3 py-2 text-sm font-semibold text-blue-800"
                            onClick={() => void handleIssueLink(visit.visit_id, eligibleContacts[0]?.contact_id)}
                            disabled={!visit.latest_status_event || eligibleContacts.length === 0}
                          >
                            <Link2 size={15} className="mr-2 inline-block" />
                            Issue Status Link
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-4 grid gap-4 lg:grid-cols-3">
                  <section className="rounded-xl border border-slate-200 p-3">
                    <h4 className="text-xs font-black uppercase tracking-widest text-slate-500">Contacts</h4>
                    <div className="mt-3 space-y-2">
                      {visit.contacts.map((contact) => (
                        <label key={contact.contact_id} className="flex items-start gap-2 rounded-lg border border-slate-100 p-2 text-sm text-slate-700">
                          <input
                            type="checkbox"
                            checked={chosenContacts.includes(contact.contact_id)}
                            disabled={contact.consent_status !== "granted" || contact.preferred_channel === "none"}
                            onChange={(event) => {
                              setSelectedContacts((current) => {
                                const currentSelection = current[visit.visit_id] ?? eligibleContacts.map((entry) => entry.contact_id);
                                return {
                                  ...current,
                                  [visit.visit_id]: event.target.checked
                                    ? Array.from(new Set([...currentSelection, contact.contact_id]))
                                    : currentSelection.filter((id) => id !== contact.contact_id),
                                };
                              });
                            }}
                          />
                          <span>
                            <span className="block font-semibold text-slate-800">{contact.display_name}</span>
                            <span className="block text-xs text-slate-500">
                              {labelize(contact.relation_to_patient)} · {contact.preferred_channel} · {contact.consent_status}
                            </span>
                          </span>
                        </label>
                      ))}
                      {visit.contacts.length === 0 && <p className="text-sm text-slate-500">No contacts recorded yet.</p>}
                    </div>
                  </section>

                  <section className="rounded-xl border border-slate-200 p-3">
                    <h4 className="text-xs font-black uppercase tracking-widest text-slate-500">Recent Status Events</h4>
                    <div className="mt-3 space-y-2">
                      {visit.recent_status_events.map((event) => (
                        <div key={event.status_event_id} className="rounded-lg border border-slate-100 p-2">
                          <p className="text-sm font-semibold text-slate-800">{labelize(event.status_code)}</p>
                          <p className="text-xs text-slate-500">{new Date(event.occurred_at).toLocaleString()}</p>
                        </div>
                      ))}
                      {visit.recent_status_events.length === 0 && <p className="text-sm text-slate-500">No status events yet.</p>}
                    </div>
                  </section>

                  <section className="rounded-xl border border-slate-200 p-3">
                    <h4 className="text-xs font-black uppercase tracking-widest text-slate-500">Recent Dispatches</h4>
                    <div className="mt-3 space-y-2">
                      {visit.recent_dispatches.map((dispatch) => (
                        <div key={dispatch.dispatch_id} className="rounded-lg border border-slate-100 p-2">
                          <div className="flex items-center gap-2">
                            <BellRing size={14} className="text-slate-400" />
                            <p className="text-sm font-semibold text-slate-800">{labelize(dispatch.channel)}</p>
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                              {dispatch.delivery_status}
                            </span>
                          </div>
                          <p className="mt-1 text-xs text-slate-500">{dispatch.message_body}</p>
                          <p className="mt-1 text-[11px] text-slate-400">{new Date(dispatch.created_at).toLocaleString()}</p>
                        </div>
                      ))}
                      {visit.recent_dispatches.length === 0 && <p className="text-sm text-slate-500">No dispatches queued yet.</p>}
                    </div>
                  </section>
                </div>
              </article>
            );
          })}

          {visits.length === 0 && !boardQuery.isLoading && (
            <div className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">
              No visits matched the current patient communications window.
            </div>
          )}
        </div>
      </section>

      <section className="v11-section p-5">
        <div className="flex items-start gap-3">
          <MessageSquareMore className="mt-0.5 text-slate-400" size={18} />
          <div>
            <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">Sprint Boundary</h2>
            <p className="mt-1 text-sm text-slate-600">
              This workspace now supports bounded family status link issuance on top of generic communication updates. Detailed clinical data, broader patient portal scope, and external SMS/email delivery vendors remain deferred.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
